import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { OrderListItem, OrderStatus, OrdersListResponse } from '@/types';

const STATUS_OPTIONS: { value: OrderStatus | ''; label: string }[] = [
  { value: '', label: 'Any status' },
  { value: 'pending', label: 'Pending' },
  { value: 'successful', label: 'Paid' },
  { value: 'marked_as_packed', label: 'Packed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'settled', label: 'Settled' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'failed', label: 'Failed' },
  { value: 'lost', label: 'Lost' },
];

const STATUS_TONE: Record<OrderStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  successful: 'bg-blue-100 text-blue-700',
  marked_as_packed: 'bg-violet-100 text-violet-700',
  shipped: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-teal-100 text-teal-700',
  settled: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-200 text-slate-700',
  refunded: 'bg-slate-200 text-slate-700',
  failed: 'bg-red-100 text-red-700',
  lost: 'bg-red-100 text-red-700',
};

function formatNaira(numStr: string) {
  const n = Number(numStr);
  if (!Number.isFinite(n)) return numStr;
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function OrdersListPage() {
  const [searchParams] = useSearchParams();
  const initialAwaiting = searchParams.get('awaiting_settlement') === 'true';

  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');
  const [awaitingSettlement, setAwaitingSettlement] = useState(initialAwaiting);
  const [data, setData] = useState<OrdersListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      page,
      per_page: perPage,
      ...(search && { search }),
      ...(statusFilter && { status: statusFilter }),
      ...(awaitingSettlement && { awaiting_settlement: 'true' }),
    }),
    [page, perPage, search, statusFilter, awaitingSettlement]
  );

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<OrdersListResponse>('/back-office/orders', {
        params,
      });
      setData(res.data);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load orders'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <div className="px-8 py-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Orders</h1>
        <p className="text-sm text-slate-500">
          Product orders across stores. Consultations have their own tab.
          Settle delivered orders to release funds to sellers.
        </p>
      </div>

      <form
        onSubmit={onSearchSubmit}
        className="mt-6 flex flex-wrap items-end gap-3 rounded border border-slate-200 bg-white p-4"
      >
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-slate-600">
            Search
          </label>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Reference"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as OrderStatus | '');
              setPage(1);
            }}
            className="mt-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 pb-1.5">
          <input
            type="checkbox"
            checked={awaitingSettlement}
            onChange={(e) => {
              setAwaitingSettlement(e.target.checked);
              setPage(1);
            }}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          Needs settlement
        </label>
        <button
          type="submit"
          className="rounded bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          Apply
        </button>
      </form>

      <div className="mt-6 overflow-hidden rounded border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-slate-500"
                >
                  Loading…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-red-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && data?.data.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-slate-500"
                >
                  No orders match these filters.
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              data?.data.map((o) => <OrderRow key={o.id} order={o} />)}
          </tbody>
        </table>
      </div>

      {data && data.pagination.total > 0 && (
        <Pagination
          page={data.pagination.page}
          totalPages={data.pagination.total_pages}
          total={data.pagination.total}
          onChange={setPage}
        />
      )}
    </div>
  );
}

function OrderRow({ order }: { order: OrderListItem }) {
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3">
        <Link
          to={`/orders/${order.reference}`}
          className="font-mono text-xs font-medium text-brand-600 hover:underline"
        >
          {order.reference}
        </Link>
      </td>
      <td className="px-4 py-3">
        {order.customer ? (
          <div>
            <div className="text-slate-800">
              {order.customer.first_name} {order.customer.last_name}
            </div>
            <div className="text-xs text-slate-500">{order.customer.email}</div>
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-slate-700">
        {formatNaira(order.total_price)}
      </td>
      <td className="px-4 py-3">
        <span
          className={clsx(
            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
            STATUS_TONE[order.status]
          )}
        >
          {order.status.replace(/_/g, ' ')}
        </span>
      </td>
      <td className="px-4 py-3 text-slate-600 text-xs">
        {formatDate(order.created_at)}
      </td>
    </tr>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (p: number) => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
      <div>
        Page {page} of {totalPages} · {total} total
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
        >
          Prev
        </button>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
          className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
